import { useState } from 'react';

import {
  AssetSelect,
  Button,
  CurrencyListItem,
  Heading,
  InfoBox,
  ListContainer,
  RadioButton,
  SearchBar,
  Typography,
} from '@altify/components';
import { commodityProductCodes } from '@utils/currencies';
import { tradeSharedContent } from '../../tradeShared.content';
import { content } from './SelectionCurrencyModal.content';
// Misc
import {
  ContentType,
  formatAssetAmount,
  getDisplayAssetCode,
  useContent,
} from '@altify/utils';
import { useNavigate } from '@tanstack/react-router';
import { formatCurrency } from '@utils/currencies';
import { CurrencyIcon } from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import {
  Product,
  ProductsWithHoldingsAndRates,
  ProductWithHoldingsAndRates,
  useFunctionalCurrency,
  useProductsWithHoldingsAndRates,
} from 'hooks/products/useProducts';
import { ProductTransactablePairDto } from 'services/generated';
import { useGetProfileQuery } from 'store/slices/profile-api';
import { useGetTransactablePairsQuery } from 'store/slices/trade-api';
import LeftArrowImage from '../../assets/LeftArrow.svg';
import RightArrowImage from '../../assets/RightArrow.svg';
import { TradeSide } from '../../types/TradeSide';
import { AssetListSkeleton } from './SkeletonAssetList';

const SelectionCurrencyModal = ({
  isOpen,
  onClose,
  fromProductCode,
  toProductCode,
  type,
  onFromProductCodeSelected,
  onToProductCodeSelected,
  onSideChanged,
  side,
}: {
  fromProductCode: string | null;
  toProductCode: string | null;
  isOpen: boolean;
  type: 'buy' | 'sell';
  onClose?: () => void;
  onFromProductCodeSelected?: (productCode: string) => void;
  onToProductCodeSelected?: (productCode: string) => void;
  onSideChanged?: (side: TradeSide) => void;
  side: TradeSide;
}) => {
  const navigate = useNavigate();
  const localizedContent = useContent(content, tradeSharedContent);

  const [search, setSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);

  const { products } = useProductsWithHoldingsAndRates();
  const { functionalCurrency } = useFunctionalCurrency();
  const { data: profile } = useGetProfileQuery();
  const toProduct = products?.find(pr => pr.code === toProductCode);
  const baseProduct = products?.find(
    pr => pr.code === functionalCurrency?.code,
  );

  const { isLoading, data: transactablePairs } = useGetTransactablePairsQuery({
    fromProductCode: side == 'from' ? undefined : fromProductCode!,
    toProductCode: side == 'from' ? toProductCode! : undefined,
    type: type == 'sell' ? 'SellsTo' : 'Buys',
  });

  const listProducts =
    products
      ?.filter(product =>
        ['Fiat', 'Crypto', 'Bundle', 'Stablecoin', 'Stock'].includes(
          product.type,
        ),
      )
      ?.filter(p => p.isTracked === true) ?? [];

  const categories = [
    {
      title: localizedContent.all,
      filter: (product: ProductsWithHoldingsAndRates[0]) => true,
    },
    {
      title: localizedContent.stocks,
      filter: (product: ProductsWithHoldingsAndRates[0]) =>
        product.type == 'Stock',
    },
    {
      title: localizedContent.crypto,
      filter: (product: ProductsWithHoldingsAndRates[0]) =>
        product.type == 'Crypto',
    },
    {
      title: localizedContent.cryptoBundles,
      filter: (product: ProductsWithHoldingsAndRates[0]) =>
        product.type == 'Bundle',
    },
    {
      title: localizedContent.commodities,
      // TODO: check if there is a better way (e.g. product type 'Commodity')
      filter: (product: Product) =>
        commodityProductCodes.includes(product.code),
    },
    {
      title: localizedContent.stableCoin,
      filter: (product: ProductsWithHoldingsAndRates[0]) =>
        product.type == 'Stablecoin',
    },
  ];

  const holdings = listProducts?.filter(p => (p.availableAmount ?? 0) > 0);
  const hasHoldings = holdings && holdings.length > 0;
  const transactableHoldings =
    transactablePairs == null
      ? null
      : holdings?.filter(p =>
          isTransactable(
            transactablePairs,
            side == 'from' ? p.code : fromProductCode!,
            side == 'from' ? toProductCode! : p.code,
            type,
          ),
        );
  const hasTransactableHoldings = !!transactableHoldings?.length;
  const transactableHoldingsAboveMinimum = transactableHoldings?.filter(
    p =>
      p &&
      profile &&
      p.availableAmountInDisplay > profile.minimumTradingAmount!,
  );
  const hasTransactableHoldingsAboveMinimum =
    !!transactableHoldingsAboveMinimum?.length;

  const title =
    type == 'buy'
      ? side == 'from'
        ? localizedContent.selectPaymentOption
        : localizedContent.selectWhatBuy
      : side == 'from'
        ? localizedContent.selectWhatSell
        : localizedContent.selectSellFor;

  const listTitle = categories[selectedCategoryIndex].title;

  function getListItems(
    filter: (product: ProductsWithHoldingsAndRates[0]) => boolean,
  ) {
    return transactablePairs == null
      ? null
      : listProducts
          ?.filter(
            pr =>
              pr.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              pr.code.toLowerCase().includes(searchQuery.toLowerCase()),
          )
          // Filter out products with 0 amount only when picking the from currency in sell orders
          .filter(
            p => p.availableAmount > 0 || !(type == 'sell' && side == 'from'),
          )
          .filter(filter)
          .map(p =>
            getListItem(
              p,
              transactablePairs,
              side,
              fromProductCode,
              toProductCode,
              type,
              functionalCurrency?.code!,
              profile?.minimumTradingAmount!,
              localizedContent,
              handleSelect,
              handleNavigateToDeposit,
            ),
          )
          .sort((a, b) => +b.eligible - +a.eligible);
  }

  function handleSelect(product: Product) {
    if (side == 'from') {
      onFromProductCodeSelected?.(product.code);
    } else {
      onToProductCodeSelected?.(product.code);
    }

    onClose?.();
  }

  function handleNavigateToDeposit(product: Product) {
    onClose?.();
    if (product.type?.toLowerCase() === 'fiat') {
      navigate({ to: `/funds/deposit/fiat` });
    } else {
      navigate({ to: `/funds/deposit/crypto/${product.code}` });
    }
  }

  const holdingsListItems = getListItems(p => p.availableAmount > 0);
  const categoryLists = categories
    .map(c => ({
      title: c.title,
      items: getListItems(c.filter)
        // Only show eligible assets in category and all list
        ?.filter(i => i.eligible),
    }))
    // Dont show empty categories
    .filter(i => i.items?.length);

  const SearchComponent = (
    <div className="flex flex-row w-full items-center flex-none gap-2 mx-2">
      <div className="flex-1">
        <SearchBar
          className="w-full"
          value={searchQuery}
          onValueChanged={e => {
            setSearchQuery(e ?? '');
          }}
          onFocus={() => {
            setSearch(true);
          }}
          fullWidth={true}
          showClearIcon={false}
        />
      </div>
      {search && searchQuery && searchQuery !== '' && (
        <div>
          <Button
            onPress={() => {
              setSearchQuery('');
            }}
            color="transparent"
            size="large"
            className="w-full">
            <Typography
              size="large"
              weight="bold"
              className="text-center text-[#626AFF]">
              {localizedContent.clear}
            </Typography>
          </Button>
        </div>
      )}
    </div>
  );

  const FromAssetFieldSelector = (
    <AssetSelect
      iconComponent={CurrencyIcon}
      iconProps={{
        code: fromProductCode!,
        size: 25,
        style: { marginRight: 5 },
      }}
      isFocused={side == 'from'}
      selectedAssetCode={fromProductCode?.toUpperCase()}
      onPress={() => onSideChanged?.('from')}
      title={type === 'buy' ? localizedContent.using : localizedContent.sell}
      className="flex-1"
    />
  );

  const ToAssetFieldSelector = (
    <AssetSelect
      iconComponent={CurrencyIcon}
      iconProps={{ code: toProductCode!, size: 25, style: { marginRight: 5 } }}
      isFocused={side == 'to'}
      selectedAssetCode={toProductCode?.toUpperCase()}
      onPress={() => onSideChanged?.('to')}
      title={type === 'buy' ? localizedContent.buy : localizedContent.for}
      className="flex-1"
    />
  );

  return (
    <DialogOrBottomSheet
      isOpen={isOpen}
      blurEnabled={true}
      onClose={() => onClose?.()}
      backgroundColor="#0C1C2C">
      <div className="flex flex-col items-center px-4">
        {isLoading ? (
          <div className="flex flex-col w-full">
            <div className="mb-2 flex flex-row">
              <Typography size="large" className="flex-1 text-white">
                {localizedContent.holdings}
              </Typography>
              <Typography size="large">
                {localizedContent.valueBalance}
              </Typography>
            </div>
            <AssetListSkeleton />
          </div>
        ) : (
          <>
            {!search && (
              <>
                <Heading level={5} className="text-center pb-6">
                  {title}
                </Heading>
                <div className="flex flex-row w-full my-8 justify-center items-center flex-none">
                  {type === 'buy' ? (
                    <>
                      {ToAssetFieldSelector}
                      <div className="w-20 items-center justify-center">
                        <img src={LeftArrowImage} />
                      </div>
                      {FromAssetFieldSelector}
                    </>
                  ) : (
                    <>
                      {FromAssetFieldSelector}
                      <div className="w-20 items-center justify-center">
                        <img src={RightArrowImage} />
                      </div>
                      {ToAssetFieldSelector}
                    </>
                  )}
                </div>
              </>
            )}
            {SearchComponent}
            {search ? (
              <div className="flex flex-col w-full my-7 gap-7 overflow-y-auto">
                <div className="flex flex-row w-full justify-start flex-none overflow-x-auto no-scrollbar">
                  {categoryLists.map((c, i) => (
                    <RadioButton
                      key={i}
                      selected={selectedCategoryIndex === i}
                      className="px-6 mx-1 h-10"
                      title={c.title}
                      onPress={() => {
                        setSelectedCategoryIndex(i);
                      }}
                    />
                  ))}
                </div>
                <div className="flex flex-row w-full flex-none">
                  {(categoryLists[selectedCategoryIndex]?.items ?? [])?.length >
                  0 ? (
                    <>
                      <Typography size="large" className="flex-1 text-white">
                        {listTitle}
                      </Typography>
                      <Typography size="large">
                        {localizedContent.valueBalance}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography className="text-error">
                        {localizedContent.noResults}
                      </Typography>
                    </>
                  )}
                </div>
                <ListContainer className="pb-6">
                  {(categoryLists[selectedCategoryIndex]?.items ?? [])?.map(
                    i => <CurrencyListItem {...i} key={i.productCode} />,
                  )}
                </ListContainer>
              </div>
            ) : (
              <div className="flex flex-col w-full my-7 gap-7  overflow-auto">
                <div>
                  {hasHoldings && side === 'from' && (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row">
                        <Typography size="large" className="flex-1 text-white">
                          {localizedContent.holdings}
                        </Typography>
                        <Typography size="large">
                          {localizedContent.valueBalance}
                        </Typography>
                      </div>
                      {!hasHoldings && (
                        <InfoBox
                          className="w-full"
                          title={localizedContent.noBalance}
                          subtitle={localizedContent.chooseAssetToDeposit}
                        />
                      )}
                      {hasHoldings && !hasTransactableHoldings && (
                        <InfoBox
                          className="w-full"
                          title={localizedContent.chooseAssetToDeposit}
                          subtitle={`${localizedContent.noAsset} ${toProduct?.name}.`}
                        />
                      )}
                      {hasTransactableHoldings &&
                        !hasTransactableHoldingsAboveMinimum && (
                          <InfoBox
                            className="w-full"
                            title={localizedContent.insufficientBalance}
                            subtitle={`${localizedContent.leastBalanceToTransact(
                              formatAssetAmount(
                                profile?.minimumTradingAmount ?? null,
                                baseProduct?.code ?? null,
                              ),
                            )} ${localizedContent.chooseAssetToDeposit}`}
                          />
                        )}
                      {holdingsListItems && (
                        <ListContainer className="pb-6">
                          {holdingsListItems!.map(i => (
                            <CurrencyListItem {...i} key={i.productCode} />
                          ))}
                        </ListContainer>
                      )}
                    </div>
                  )}
                  {categoryLists[0] && (
                    <>
                      <div className="mb-2 flex flex-row">
                        <Typography size="large" className="flex-1 text-white">
                          {localizedContent.all}
                        </Typography>
                        <Typography size="large">
                          {localizedContent.valueBalance}
                        </Typography>
                      </div>
                      <ListContainer className="pb-6">
                        {categoryLists[0]!.items!.map(i => (
                          <CurrencyListItem {...i} key={i.productCode} />
                        ))}
                      </ListContainer>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DialogOrBottomSheet>
  );
};

/* 
Get all products (which match search query if applicable) and add following attributes
- Is eligible for trading
- Holdings (units + fiat value)
- (ONLY FROM) Is holdings above minimum

Generate list items:
- Write 'not eligible' for non eligible products
- (ONLY FROM) make holdings red if below minimum
- (ONLY FROM) link to deposit page if holdings below minimum

Generate List
- TO: with all products
- FROM: 
  - with all products
  - with products that have holdings > 0
*/
function getListItem(
  p: ProductWithHoldingsAndRates,
  transactablePairs: ProductTransactablePairDto[],
  side: TradeSide,
  fromProductCode: string | null,
  toProductCode: string | null,
  orderType: 'buy' | 'sell',
  functionalProductCode: string,
  minimumTradingAmount: number,
  localizedContent: ContentType<typeof content>,
  onSelected: (product: Product) => void,
  onNavigateToDeposit: (product: Product) => void,
) {
  const productCode = p.code;
  const effectiveFromProductCode =
    side == 'from' ? productCode : fromProductCode!;
  const effectiveToProductCode = side == 'from' ? toProductCode! : productCode;

  var eligible = getIsEligible(
    transactablePairs,
    side,
    fromProductCode,
    toProductCode,
    p.code,
    orderType,
  );
  var holdings = p.availableAmount ?? 0;
  var holdingsInFunctional = p.availableAmountInDisplay ?? 0;
  var holdingsAboveMinimum = p.availableAmountInDisplay > minimumTradingAmount;
  var insufficientBalance = !holdingsAboveMinimum && side == 'from';

  return {
    iconComponent: CurrencyIcon,
    iconProps: { code: p.code },
    title: `${p.name} (${getDisplayAssetCode(p.code)})`,
    subtitle: p.type,
    productCode,
    eligible,
    error: insufficientBalance && eligible,
    active: eligible,
    onPress: () => {
      if (!eligible) return;

      if (holdingsAboveMinimum || side == 'to') {
        onSelected(p);
      } else if (side == 'from' && orderType == 'buy') {
        onNavigateToDeposit(p);
      }
    },
    infoText: !eligible
      ? localizedContent.cannotBeTraded(
          effectiveFromProductCode,
          effectiveToProductCode,
        )
      : insufficientBalance
        ? localizedContent.insufficientBalanceToTrade(p.code)
        : undefined,
    balanceText: getBalanceText(
      p.code,
      holdings,
      holdingsInFunctional,
      holdingsAboveMinimum,
      functionalProductCode,
      side,
    ),
  };
}

function getIsEligible(
  transactablePairs: ProductTransactablePairDto[],
  side: TradeSide,
  fromProductCode: string | null,
  toProductCode: string | null,
  productCode: string,
  orderType: 'buy' | 'sell',
) {
  return (
    (side == 'from' && !toProductCode) ||
    (side == 'to' && !fromProductCode) ||
    isTransactable(
      transactablePairs,
      side == 'from' ? productCode : fromProductCode!,
      side == 'from' ? toProductCode! : productCode,
      orderType,
    )
  );
}

function isTransactable(
  transactablePairs: ProductTransactablePairDto[],
  fromProductCode: string,
  toProductCode: string,
  orderType: 'buy' | 'sell',
) {
  return transactablePairs?.some(
    tp =>
      tp.transactType == (orderType == 'buy' ? 'Buys' : 'SellsTo') &&
      tp.fromProductCode == fromProductCode?.toUpperCase() &&
      tp.toProductCode == toProductCode?.toUpperCase(),
  );
}

function getBalanceText(
  productCode: string,
  unitAmount: number,
  amountInFunctional: number,
  aboveMinimum: boolean,
  functionalProductCode: string,
  side: TradeSide,
) {
  const sameProductPrimary = formatCurrency(unitAmount, productCode);
  const defaultPrimary = formatCurrency(
    amountInFunctional,
    functionalProductCode,
  );
  const primaryText =
    productCode === functionalProductCode ? sameProductPrimary : defaultPrimary;
  const primary = unitAmount === 0 ? undefined : primaryText;

  const secondaryText =
    productCode !== functionalProductCode
      ? formatCurrency(unitAmount, productCode)
      : undefined;
  const secondary = unitAmount === 0 ? undefined : secondaryText;

  return {
    primary,
    secondary,
    className: aboveMinimum! || side == 'to' ? 'text-white' : 'text-error',
  };
}
export default SelectionCurrencyModal;
